import { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { pillStyle } from '@/components/StatusDot';
import api from '@/lib/api';

const STATUSES = ['Not Started', 'In Progress', 'Completed', 'N/A'];
const STATUS_COLORS = {
  'Not Started': '#031333',
  'In Progress': '#D97706',
  'Completed': '#16A34A',
  'N/A': '#031333',
};

function StatusOption({ status }) {
  return (
    <span className="inline-flex items-center" style={pillStyle(STATUS_COLORS[status])}>{status}</span>
  );
}

export function CotcoChecklist({ projectId, onUpdated }) {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedId, setSavedId] = useState(null);

  const fetchChecklist = useCallback(() => {
    return api.get(`/projects/${projectId}/cotco`)
      .then(r => setChecklist(Array.isArray(r.data) ? r.data : []))
      .catch(() => setChecklist([]));
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    fetchChecklist().finally(() => setLoading(false));
  }, [fetchChecklist]);

  const flash = (mid) => {
    setSavedId(mid);
    setTimeout(() => setSavedId(cur => (cur === mid ? null : cur)), 1500);
  };

  const setLocal = (mid, field, value) => {
    setChecklist(prev => prev.map(m => (m.milestone_id === mid ? { ...m, [field]: value } : m)));
  };

  const save = async (mid, patch) => {
    try {
      const r = await api.put(`/projects/${projectId}/cotco/${mid}`, patch);
      setChecklist(Array.isArray(r.data) ? r.data : []);
      flash(mid);
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error('Could not save milestone');
      fetchChecklist();
    }
  };

  const completed = (Array.isArray(checklist) ? checklist : []).filter(m => m.status === 'Completed').length;
  const untouched = (Array.isArray(checklist) ? checklist : []).length > 0 && checklist.every(m => m.status === 'Not Started');

  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading checklist...</p>;

  return (
    <div className="space-y-3" data-testid="cotco-checklist">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#010A21' }}>CO/TCO Sign-Off Checklist</p>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="cotco-progress">{completed} of 10 milestones complete</p>
        </div>
        {untouched && (
          <Button size="sm" variant="outline" className="border-border" onClick={() => toast.info('Checklist is ready: edit any milestone below.')} data-testid="cotco-initialize-btn">
            Initialize Checklist
          </Button>
        )}
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Milestone</TableHead>
              <TableHead className="text-xs text-muted-foreground w-40">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground">Responsible Party</TableHead>
              <TableHead className="text-xs text-muted-foreground">Target Date</TableHead>
              <TableHead className="text-xs text-muted-foreground">Completion Date</TableHead>
              <TableHead className="text-xs text-muted-foreground">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(Array.isArray(checklist) ? checklist : []).map(m => {
              const isDone = m.status === 'Completed';
              const isNA = m.status === 'N/A';
              return (
                <TableRow
                  key={m.milestone_id}
                  className="border-border hover:bg-secondary/20"
                  style={{ borderLeft: isDone ? '3px solid #16A34A' : '3px solid transparent', opacity: isNA ? 0.7 : 1 }}
                  data-testid={`cotco-row-${m.milestone_id}`}
                >
                  <TableCell className="align-middle">
                    <span className="inline-flex items-center gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: '#010A21' }}>
                      {m.label}
                      {savedId === m.milestone_id && <Check className="h-3.5 w-3.5 text-green-600" data-testid={`cotco-saved-${m.milestone_id}`} />}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Select value={m.status} onValueChange={v => {
                      const patch = { status: v };
                      if (v === 'Completed' && !m.completion_date) {
                        patch.completion_date = new Date().toISOString().slice(0, 10);
                      }
                      save(m.milestone_id, patch);
                    }}>
                      <SelectTrigger className="h-8 bg-background border-border text-sm" data-testid={`cotco-status-${m.milestone_id}`}>
                        <SelectValue><StatusOption status={m.status} /></SelectValue>
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm"><StatusOption status={s} /></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={m.responsible_party || ''}
                      onChange={e => setLocal(m.milestone_id, 'responsible_party', e.target.value)}
                      onBlur={e => save(m.milestone_id, { responsible_party: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      placeholder="Assign party"
                      className="h-8 bg-background border-border text-sm"
                      data-testid={`cotco-responsible-${m.milestone_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="date"
                      value={m.target_date || ''}
                      onChange={e => { setLocal(m.milestone_id, 'target_date', e.target.value); save(m.milestone_id, { target_date: e.target.value }); }}
                      className="h-8 bg-background border-border text-sm w-36"
                      data-testid={`cotco-target-${m.milestone_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    {isDone ? (
                      <Input
                        type="date"
                        value={m.completion_date || ''}
                        onChange={e => { setLocal(m.milestone_id, 'completion_date', e.target.value); save(m.milestone_id, { completion_date: e.target.value }); }}
                        className="h-8 bg-background border-border text-sm w-36"
                        data-testid={`cotco-completion-${m.milestone_id}`}
                      />
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      value={m.notes || ''}
                      onChange={e => setLocal(m.milestone_id, 'notes', e.target.value)}
                      onBlur={e => save(m.milestone_id, { notes: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                      placeholder="Add notes"
                      className="h-8 bg-background border-border text-sm"
                      data-testid={`cotco-notes-${m.milestone_id}`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default CotcoChecklist;
